import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FaEye, FaBuilding, FaTimes, FaCheckDouble } from "react-icons/fa";
import styles from "../../../feesManagement/FeeType/styles.module.css";
import { CustomDropdown } from "../../../../../components";
import {
  fetchClassOptions,
  fetchSectionOptions,
  clearSectionOptions,
} from "../../../../../redux/Masterdata";
import AxiosInstance, { TokenService } from "../../../../../services/Api";
import { useDispatch, useSelector } from "react-redux";
import CustomMRTTable from "../../../../../components/CustomMRTTable/CustomMRTTable";
import PopupModal from "../../../../../components/PopupModal/PopupModal";

/* ── Module-level constants ──
   These MUST live outside the component. Declaring them inside,
   below the line that reads them, throws a temporal-dead-zone
   ReferenceError on first render. */
const CHECKLIST_URL = "admission/Checklist";
const CHECKLIST_UPDATE_URL = "admission/checkListUpdate";
const SECONDARY_ADMISSION_LIST_URL = "admission/listSecondryAdmission";
const HIGHER_ADMISSION_LIST_URL = "admission/listHigherSecondryAdmission";

// admission_type: 0 = secondary, 1 = higher secondary
const ADMISSION_TYPE_SECONDARY = 0;
const ADMISSION_TYPE_HIGHER = 1;

// fee_status from the API is numeric: 1 = paid, 0 = not paid
const FEE_STATUS_PAID = 1;

/* ── Inline styles ── */
const S = {
  statusBadge: {
    display: "inline-block",
    padding: "6px 16px",
    borderRadius: 6,
    fontSize: 13,
    fontWeight: 700,
    color: "#fff",
    whiteSpace: "nowrap",
  },
  checkListBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    marginLeft: "auto",
    padding: "10px 20px",
    background: "#f97316",
    color: "#fff",
    border: "none",
    borderRadius: 8,
    fontSize: 15,
    fontWeight: 700,
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  viewBtn: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 34,
    height: 34,
    background: "#f97316",
    color: "#fff",
    border: "none",
    borderRadius: 6,
    cursor: "pointer",
  },
  // ── checklist modal ──
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(15, 23, 42, 0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1200,
    padding: 16,
  },
  modal: {
    background: "#fff",
    borderRadius: 10,
    width: "100%",
    maxWidth: 560,
    maxHeight: "90vh",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  modalHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "18px 24px",
    borderBottom: "1px solid #e5e7eb",
  },
  modalTitle: {
    margin: 0,
    fontSize: 20,
    fontWeight: 700,
    color: "#0f172a",
  },
  closeBtn: {
    background: "none",
    border: "none",
    fontSize: 18,
    cursor: "pointer",
    color: "#0f172a",
    lineHeight: 1,
    padding: 4,
    display: "inline-flex",
  },
  modalBody: {
    padding: "20px 24px",
    overflowY: "auto",
    flex: "1 1 auto",
    minHeight: 0,
  },
  checkRow: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "10px 0",
    cursor: "pointer",
    fontSize: 15,
    fontWeight: 600,
    color: "#0f172a",
  },
  checkInput: { width: 18, height: 18, cursor: "pointer", margin: 0 },
  modalFooter: {
    display: "flex",
    justifyContent: "center",
    gap: 14,
    padding: "16px 24px 22px",
    borderTop: "1px solid #e5e7eb",
  },
  cancelBtn: {
    padding: "11px 30px",
    fontSize: 15,
    fontWeight: 600,
    color: "#fff",
    background: "#1e3a5f",
    border: "none",
    borderRadius: 8,
    cursor: "pointer",
  },
  updateBtn: {
    padding: "11px 30px",
    fontSize: 15,
    fontWeight: 600,
    color: "#fff",
    background: "#f97316",
    border: "none",
    borderRadius: 8,
    cursor: "pointer",
  },
};

/* ── Fees status badge ──
   The API sends fee_status as a number (1 = paid, 0 = not paid). */
const FeeStatusBadge = ({ status }) => {
  const isPaid = Number(status) === FEE_STATUS_PAID;
  return (
    <span
      style={{
        ...S.statusBadge,
        backgroundColor: isPaid ? "#16a34a" : "#dc2626",
      }}
    >
      {isPaid ? "Paid" : "Not Paid"}
    </span>
  );
};

/* ────────────────────────────────────────────
   Check List modal
──────────────────────────────────────────── */
const CheckListModal = ({
  open,
  onClose,
  onUpdate,
  submitting,
  loading,
  items = [],
  checked = {},
  setChecked,
}) => {
  if (!open) return null;

  const toggle = (id) => setChecked((prev) => ({ ...prev, [id]: !prev[id] }));

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget && !submitting) onClose?.();
  };

  return (
    <div style={S.overlay} onClick={handleOverlayClick}>
      <div style={S.modal}>
        <div style={S.modalHeader}>
          <h3 style={S.modalTitle}>Check List</h3>
          <button
            type="button"
            style={S.closeBtn}
            onClick={onClose}
            disabled={submitting}
            aria-label="Close"
          >
            <FaTimes />
          </button>
        </div>

        <div style={S.modalBody}>
          {loading && <div style={S.emptyRow}>Loading check list...</div>}

          {!loading && items.length === 0 && (
            <div style={S.emptyRow}>
              No check list items found for this student.
            </div>
          )}

          {!loading &&
            items.map((item) => (
              <label
                key={item.id}
                style={{
                  ...S.checkRow,
                  cursor: item.disabled ? "not-allowed" : "pointer",
                  opacity: item.disabled ? 0.45 : 1,
                }}
                title={item.disabledReason}
                /* 2. A <label> forwards its clicks to the input it wraps.
                                      Clicking the TEXT toggles the box unless blocked. */
                onClick={(e) => {
                  if (item.disabled) e.preventDefault();
                }}
              >
                <input
                  type="checkbox"
                  style={{
                    ...S.checkInput,
                    cursor: item.disabled ? "not-allowed" : "pointer",
                    pointerEvents: item.disabled ? "none" : "auto",
                  }}
                  checked={Boolean(checked[item.id])}
                  /* 3. THE FIX — this attribute is what was missing. */
                  disabled={submitting || item.disabled}
                  onChange={() => {
                    if (item.disabled) return;
                    toggle(item.id);
                  }}
                />
                {item.label}
              </label>
            ))}
        </div>

        <div style={S.modalFooter}>
          <button
            type="button"
            style={S.cancelBtn}
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="button"
            style={S.updateBtn}
            onClick={onUpdate}
            disabled={submitting || loading || items.length === 0}
          >
            {submitting ? "Updating..." : "Update"}
          </button>
        </div>
      </div>
    </div>
  );
};

const ListAdmissionCollectFees = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const { translations } = useSelector((state) => state.language);

  // admission_type: 0 = secondary, 1 = higher secondary.
  // Number() matters: location.state survives a reload as JSON,
  // and "1" === 1 is false.
  const admissionType = Number(
    location.state?.admissionType ?? ADMISSION_TYPE_SECONDARY,
  );

  const listUrl =
    admissionType === ADMISSION_TYPE_HIGHER
      ? HIGHER_ADMISSION_LIST_URL
      : SECONDARY_ADMISSION_LIST_URL;
  const isSecondary = admissionType === ADMISSION_TYPE_SECONDARY ? 1 : 0;

  const user = TokenService.getUser();
  const clientData = user?.client_data?.[0] || {};
  const school_id = clientData?.school_id || "";
  const master_id = clientData?.master_id || "";
  const academic_year_id =
    clientData?.academic_year_id || clientData?.academic_id || "";
  const academic_year = clientData?.academic_year || "";
  const roleId = Number(user?.role?.id);

  const {
    classOptions: reduxClassOptions,
    staffClassAndSubjectOptions,
    sectionOptions: reduxSectionOptions,
  } = useSelector((state) => state.masterData);

  const [data, setData] = useState([]);
  const [tableLoading, setTableLoading] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [apiSearch, setApiSearch] = useState("");
  const [rowCount, setRowCount] = useState(0);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  const [selectedClass, setSelectedClass] = useState("");
  const [selectedSection, setSelectedSection] = useState("");

  // ── row selection + checklist ──
  const [selectedIds, setSelectedIds] = useState([]);

  const [modalState, setModalState] = useState({
    open: false,
    type: "success",
    message: "",
  });
  /* ── EDIT 3: replace the checklist state block ───────────────────
       Add items + loading; keep checkListOpen/Submitting.             */
  const [checkListOpen, setCheckListOpen] = useState(false);
  const [checkListSubmitting, setCheckListSubmitting] = useState(false);
  const [checkListItems, setCheckListItems] = useState([]);
  const [checkListLoading, setCheckListLoading] = useState(false);
  const [checkListChecked, setCheckListChecked] = useState({});
  // ── Class options on mount ──
  useEffect(() => {
    if (school_id) {
      dispatch(
        fetchClassOptions({
          id: parseInt(school_id),
          academic_year: academic_year,
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, school_id]);

  const classOptions = useMemo(() => {
    const sourceData =
      roleId === 3 ? staffClassAndSubjectOptions : reduxClassOptions;
    return [
      {
        value: "",
        label: translations.labels?.selectClass || "Select Class",
      },
      ...sourceData.map((item) => ({
        value: item.class_id ?? item.value,
        label: item.class_name ?? item.label,
      })),
    ];
  }, [reduxClassOptions, staffClassAndSubjectOptions, roleId, translations]);

  const sectionOptions = useMemo(
    () => [
      {
        value: "",
        label: translations.labels?.selectSection || "Select Section",
      },
      ...reduxSectionOptions,
    ],
    [reduxSectionOptions, translations],
  );

  /* The API's `preferred_class` is a class NAME ("VI"), not an id,
       so the filter has to send the dropdown's label, not its value. */
  const selectedClassName = useMemo(
    () =>
      classOptions.find((o) => String(o.value) === String(selectedClass))
        ?.label || "",
    [classOptions, selectedClass],
  );
  /* ── EDIT 4: fetch the checklist when the modal opens ────────────
       POST admission/Checklist { id, is_secondary }
       The API takes ONE id, so exactly one student must be selected.
    
       ⚠ The response shape wasn't supplied. This reads the likely
       field names with fallbacks — correct them once you have it.     */
  const openCheckList = useCallback(async () => {
    if (selectedIds.length !== 1) {
      setModalState({
        open: true,
        type: "error",
        message: "Select a student to open their check list.",
      });
      return;
    }

    setCheckListOpen(true);
    setCheckListLoading(true);
    setCheckListItems([]);
    setCheckListChecked({});
    // the selected student's fee state gates the "Payment collected" item
    const selectedRow = data.find((r) => r.id === selectedIds[0]);
    const isPaid = Number(selectedRow?.fee_status) === FEE_STATUS_PAID;
    try {
      const response = await AxiosInstance.post(CHECKLIST_URL, {
        id: Number(selectedIds[0]),
        is_secondary: isSecondary,
      });

      const ok = response?.data?.status || response?.data?.success;
      const list = response?.data?.data;

      if (ok && Array.isArray(list)) {
        const items = list.map((item) => {
          // NOTE: `domain_id` holds the LABEL text, not an id.
          const label = item.domain_id || "";
          // "Payment collected" can't be ticked until the fee is paid
          const isPaymentItem = label.toLowerCase().includes("payment");
          return {
            id: item.id, // what checkListId sends back
            label,
            checked: Number(item.is_selected) === 1,
            disabled: isPaymentItem && !isPaid,
            disabledReason:
              isPaymentItem && !isPaid
                ? "Fees are not paid yet for this student."
                : undefined,
          };
        });
        setCheckListItems(items);
        setCheckListChecked(
          items.reduce((acc, i) => {
            if (i.checked) acc[i.id] = true;
            return acc;
          }, {}),
        );
      } else {
        setCheckListItems([]);
      }
    } catch (error) {
      console.error("Error fetching check list:", error);
      setCheckListItems([]);
    } finally {
      setCheckListLoading(false);
    }
  }, [selectedIds, isSecondary]);
  /* ────────────────────────────────────────
       API BINDING
       0 -> admission/listSecondryAdmission
       1 -> admission/listHigherSecondryAdmission

       { status, pagination: { total, ... }, data: [
           { id, application_no, student_name, assigned_section,
             preferred_class, preferred_first_language, date,
             gender, application_mode, status, fee_status } ] }

       status is pinned to "Approved": only approved admissions
       can have fees collected.
    ──────────────────────────────────────── */
  const fetchAdmissionList = useCallback(async () => {
    if (!school_id) {
      setData([]);
      setRowCount(0);
      return;
    }

    setTableLoading(true);
    try {
      const response = await AxiosInstance.post(listUrl, {
        school_id: Number(school_id),
        academic_year_id: Number(academic_year_id),
        application_no: "",
        name: apiSearch.trim(),
        // class NAME, not id — matches the response's shape
        preferred_class: selectedClass ? selectedClassName : "",
        section: selectedSection || "",
        preferred_first_language: "",
        date: "",
        gender: "",
        application_mode: "",
        status: "Approved",
        page: pagination.pageIndex + 1,
        limit: pagination.pageSize,
      });

      const ok = response?.data?.status || response?.data?.success;
      const list = response?.data?.data;

      if (ok && Array.isArray(list)) {
        const transformed = list.map((item) => ({
          // `id` -> what FeeView / feePayment expect
          id: item.id,
          application_no: item.application_no || "",
          // preferred_class is the class NAME ("VI")
          class_name: item.preferred_class || "",
          // section arrives as assigned_section
          section: item.assigned_section || "",
          student: item.student_name || "",
          // numeric: 1 = paid, 0 = not paid
          fee_status: item.fee_status,
          // kept for the payment payload; see notes
          class_id: item.class_id ?? "",
          emis_id: item.emis_id ?? "",
        }));
        setData(transformed);
        setRowCount(response.data.pagination?.total || transformed.length);
      } else {
        setData([]);
        setRowCount(0);
      }
    } catch (error) {
      console.error("Error fetching admission list:", error);
      setData([]);
      setRowCount(0);
    } finally {
      setTableLoading(false);
    }
  }, [
    school_id,
    academic_year_id,
    listUrl,
    selectedClass,
    selectedClassName,
    selectedSection,
    apiSearch,
    pagination.pageIndex,
    pagination.pageSize,
  ]);

  useEffect(() => {
    fetchAdmissionList();
  }, [fetchAdmissionList]);

  // ── Clear selection when the filters change ──
  useEffect(() => {
    setSelectedIds([]);
  }, [selectedClass, selectedSection, apiSearch]);

  // ── Handlers ──
  const handleClassChange = useCallback(
    (value) => {
      const classId = typeof value === "object" ? value?.value : value;
      setSelectedClass(classId);
      setSelectedSection("");
      dispatch(clearSectionOptions());
      if (classId) {
        dispatch(
          fetchSectionOptions({
            id: [parseInt(classId)],
            school_id: parseInt(school_id),
            staff_id: parseInt(master_id),
            subject_staff: 1,
          }),
        );
      }
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    },
    [dispatch, school_id, master_id],
  );

  const handleSectionChange = useCallback((value) => {
    const sectionId = typeof value === "object" ? value?.value : value;
    setSelectedSection(sectionId);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, []);

  const handleSearchEnter = useCallback((value) => {
    setApiSearch(value || "");
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, []);

  // ── Checkbox selection ──
  const handleSelectRow = useCallback((id) => {
    setSelectedIds((prev) => (prev.includes(id) ? [] : [id]));
  }, []);

  /* View -> the fee collection screen. Carries the real
       admissionType so the next screen calls FeeView /
       feePayment with the correct admission_type. */
  const handleView = useCallback(
    (row) => {
      navigate("/user/viewCollectFees", {
        state: {
          id: row.id,
          admission_type: admissionType,
          application_no: row.application_no,
          class_id: row.class_id,
          section: row.section,
          emis_id: row.emis_id,
          data: row,
        },
      });
    },
    [navigate, admissionType],
  );

  /* ── Check List update ──
       ⚠ NO ENDPOINT WAS SUPPLIED. Wire the URL and payload once
       you have it; the selected ids and ticked items are both
       available here. */
  const handleCheckListUpdate = useCallback(async () => {
    const disabledIds = new Set(
      checkListItems.filter((i) => i.disabled).map((i) => String(i.id)),
    );
    const checkListId = Object.keys(checkListChecked)
      .filter((k) => checkListChecked[k] && !disabledIds.has(k))
      .map(Number);

    if (checkListId.length === 0) {
      setModalState({
        open: true,
        type: "error",
        message: "Tick at least one item to update.",
      });
      return;
    }

    setCheckListSubmitting(true);
    try {
      const response = await AxiosInstance.post(CHECKLIST_UPDATE_URL, {
        id: Number(selectedIds[0]),
        is_secondary: isSecondary,
        checkListId: checkListId,
        created_by: Number(user?.id),
      });

      if (response?.data?.status || response?.data?.success) {
        setCheckListOpen(false);
        setSelectedIds([]);
        setModalState({
          open: true,
          type: "success",
          message:
            response?.data?.message || "Check list updated successfully!",
        });
        fetchAdmissionList(); // fee_status may have moved
      } else {
        setModalState({
          open: true,
          type: "error",
          message:
            response?.data?.message || "Could not update the check list.",
        });
      }
    } catch (error) {
      console.error("Error updating check list:", error);
      setModalState({
        open: true,
        type: "error",
        message:
          error?.response?.data?.message ||
          "Could not update the check list. Please try again.",
      });
    } finally {
      setCheckListSubmitting(false);
    }
  }, [checkListChecked, selectedIds, isSecondary, user, fetchAdmissionList]);

  // ── Columns ──
  const columns = useMemo(
    () => [
      {
        accessorKey: "select",
        header: "",
        size: 50,
        enableSorting: false,
        enableColumnFilter: false,
        // no select-all: only one row can be chosen at a time
        Header: () => null,
        Cell: ({ row }) => (
          <input
            type="checkbox"
            checked={selectedIds.includes(row.original.id)}
            onChange={(e) => {
              e.stopPropagation();
              handleSelectRow(row.original.id);
            }}
            style={{ width: 18, height: 18, cursor: "pointer" }}
          />
        ),
      },
      {
        accessorKey: "application_no",
        header: translations.labels?.applicationId || "Application ID",
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        accessorKey: "class_name",
        header: translations.labels?.class || "Class",
        enableSorting: true,
        enableColumnFilter: true,
        Cell: ({ cell }) => cell.getValue() || "-",
      },
      {
        accessorKey: "section",
        header: translations.labels?.section || "Section",
        enableSorting: true,
        enableColumnFilter: true,
        Cell: ({ cell }) => cell.getValue() || "-",
      },
      {
        accessorKey: "student",
        header: translations.labels?.student || "Student",
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        accessorKey: "fee_status",
        header: translations.labels?.feeStatus || "Fees Status",
        enableSorting: true,
        enableColumnFilter: true,
        Cell: ({ cell }) => <FeeStatusBadge status={cell.getValue()} />,
      },
      {
        id: "actions",
        header: translations.labels?.option || "Option",
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ row }) => (
          <button
            type="button"
            style={S.viewBtn}
            onClick={() => handleView(row.original)}
            title="View"
          >
            <FaEye />
          </button>
        ),
      },
    ],
    [translations, handleView, selectedIds, handleSelectRow],
  );

  return (
    <>
      <PopupModal
        open={modalState.open}
        title={
          modalState.type === "success"
            ? translations.labels?.success || "Success"
            : translations.labels?.failed || "Failed"
        }
        message={modalState.message}
        onOk={() => setModalState((p) => ({ ...p, open: false }))}
      />

      <CheckListModal
        open={checkListOpen}
        onClose={() => setCheckListOpen(false)}
        onUpdate={handleCheckListUpdate}
        submitting={checkListSubmitting}
        loading={checkListLoading}
        items={checkListItems}
        checked={checkListChecked}
        setChecked={setCheckListChecked}
      />

      <div className={styles.container}>
        {/* Header + Check List button.
                    display:flex is set inline because styles.header
                    isn't a flex container, so marginLeft:auto on the
                    button would otherwise do nothing. */}
        <div
          className={styles.header}
          style={{
            display: "flex",
            alignItems: "center",
            width: "100%",
          }}
        >
          <div className={styles.headerIcon}>
            <FaBuilding />
          </div>
          <h1 className={styles.headerTitle} style={{ margin: 0 }}>
            {translations.labels?.collectFees || "Collect Fees"}
          </h1>
          <button type="button" style={S.checkListBtn} onClick={openCheckList}>
            <FaCheckDouble />
            {translations.labels?.checkList || "Check List"}
          </button>
        </div>

        {/* ── Filters ── */}
        <div className="row" style={{ marginBottom: 16 }}>
          <div className="col-lg-3 px-2 col-md-6 col-sm-12">
            <CustomDropdown
              label={translations.labels?.class || "Class"}
              name="filterClass"
              value={selectedClass}
              onChange={handleClassChange}
              options={classOptions}
              placeholder={translations.labels?.selectClass || "Select Class"}
              required
            />
          </div>
          <div className="col-lg-3 px-2 col-md-6 col-sm-12">
            <CustomDropdown
              label={translations.labels?.section || "Section"}
              name="filterSection"
              value={selectedSection}
              onChange={handleSectionChange}
              options={sectionOptions}
              placeholder={
                translations.labels?.selectSection || "Select Section"
              }
              disabled={!selectedClass}
            />
          </div>
        </div>

        {/* ── Table ── */}
        <div className={styles.tableSection}>
          <CustomMRTTable
            columns={columns}
            data={data}
            loading={tableLoading}
            pagination={pagination}
            rowCount={rowCount}
            placeholder={translations?.labels?.searchNameCode || "Search ..."}
            setSearchInput={setSearchInput}
            onSearchEnter={handleSearchEnter}
            onPaginationChange={setPagination}
            searchInput={searchInput}
            enableSearch={true}
            enableFiltering={true}
            manualPagination={true}
          />
        </div>
      </div>
    </>
  );
};

export default ListAdmissionCollectFees;
